/**
 * Cloud Search (xCloud Search & Filter) - filtered pages paged like the collection pages:
 * 24 products per page, so every page but the last is full rows of 4.
 *
 * The app serves its filtered pages (/a/search/...) at a fixed page size: 18 today, a value
 * stored in the app that the theme cannot change. The unfiltered collection pages show 24 per
 * page (templates/collection.with-filter.json), 4 per row. This script re-pages the app's list
 * into pages of 24 so filtering keeps the same layout:
 *   60 products -> 24, 24, 12      21 products -> one page of 21
 * Page N shows products 24(N-1)+1 .. 24N. It is built from the app page the URL loads (the one
 * where page N starts) plus the next app page, fetched through the app's section-only URL
 * (cloud_search_section_id=main); products of that app page outside page N are hidden. The page
 * bar is rebuilt with our page numbers; each links to the app page where that page starts, so
 * the theme's ajax paging and Back keep working. URLs therefore carry the app's page number
 * (our page 4 is ?page=5).
 *
 * Every page is complete on its own - nothing is carried between pages, so nothing can be
 * skipped or shown twice. If the fetch fails, the page goes back to exactly what the app sent
 * (18 products and its own page bar). Without this script the pages are as the app renders them.
 */
(function () {
  'use strict';

  if (window.CloudSearchFullRows) return;

  var PER_PAGE = 24; // as the unfiltered collection pages (templates/collection.with-filter.json)
  var SECTION_ID = 'main';
  var SECTION_SELECTOR = '#shopify-section-' + SECTION_ID;
  var GRID_SELECTOR = '.collection-listing .product-grid';
  var BAR_SELECTOR = '.pagination';
  var HIDDEN_CLASS = 'cloud-search-pages-hidden';
  var ADDED_ATTR = 'data-cloud-search-added';
  var DONE_ATTR = 'data-cloud-search-pages';
  var FETCH_TIMEOUT_MS = 10000;
  var CLICK_GUARD_MS = 500;
  var CACHE_PAGES = 6;

  var appPageSize = 0;
  var total = 0;
  var pages = 0;
  var generation = 0;
  var jumpedAt = 0;
  var labels = { prev: '« Previous', next: 'Next »' };
  var fetches = {}; // url -> promise of that app page's product-block HTML
  var fetchOrder = [];

  function readAppData() {
    var el = document.getElementById('cloudsearch-data');
    if (!el) return null;
    try {
      var data = JSON.parse(el.textContent);
      var size = parseInt(data.pageSize, 10);
      var count = parseInt(data.total, 10);
      return size > 0 && count > 0 ? { pageSize: size, total: count } : null;
    } catch (e) {
      return null;
    }
  }

  // The app page the grid holds: the bar the app sent with it marks it; else the URL.
  function appPageShown(bar) {
    var current = bar && bar.querySelector('.pagination__number.underline--not-link');
    var number = current ? parseInt(current.textContent, 10) : NaN;
    return number > 0 ? number : parseInt(new URL(window.location.href).searchParams.get('page'), 10) || 1;
  }

  // Our page for an app page: the one that starts inside it (the pages our links point to),
  // or for any other app page (an old link) the one it starts in.
  function pageForAppPage(appPage) {
    var ownStart = (appPage - 1) * appPageSize;
    var ownEnd = Math.min(ownStart + appPageSize, total);
    var starting = Math.ceil(ownStart / PER_PAGE) + 1;
    return (starting - 1) * PER_PAGE < ownEnd ? starting : Math.floor(ownStart / PER_PAGE) + 1;
  }

  // The app page where our page starts
  function appPageFor(page) {
    return Math.floor(((page - 1) * PER_PAGE) / appPageSize) + 1;
  }

  function isProductBlock(el) {
    return el.tagName === 'PRODUCT-BLOCK' && !el.hasAttribute(ADDED_ATTR);
  }

  function fetchText(url) {
    var controller = window.AbortController ? new AbortController() : null;
    var timer = controller ? setTimeout(function () { controller.abort(); }, FETCH_TIMEOUT_MS) : null;
    return fetch(url, { credentials: 'same-origin', signal: controller ? controller.signal : undefined })
      .then(function (response) {
        if (timer) clearTimeout(timer);
        if (!response.ok) throw new Error('HTTP ' + response.status);
        return response.text();
      }, function (err) {
        if (timer) clearTimeout(timer);
        throw err;
      });
  }

  function sectionUrl(appPage) {
    var url = new URL(window.location.href);
    url.hash = '';
    url.searchParams.set('page', String(appPage));
    url.searchParams.set('cloud_search_section_id', SECTION_ID);
    return url.pathname + url.search;
  }

  function appPageBlocks(appPage) {
    var url = sectionUrl(appPage);
    if (!fetches[url]) {
      fetches[url] = fetchText(url)
        .catch(function () {
          // one retry, e.g. after a 429 from the storefront
          return new Promise(function (resolve) { setTimeout(resolve, 1500); }).then(function () {
            return fetchText(url);
          });
        })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var grid = doc.querySelector(GRID_SELECTOR);
          var blocks = grid ? Array.prototype.filter.call(grid.children, isProductBlock) : [];
          if (!blocks.length) throw new Error('no products on page ' + appPage);
          return blocks.map(function (block) { return block.outerHTML; });
        });
      fetches[url].catch(function () { delete fetches[url]; });
      fetchOrder.push(url);
      if (fetchOrder.length > CACHE_PAGES) delete fetches[fetchOrder.shift()];
    }
    return fetches[url];
  }

  function toBlock(html) {
    var template = document.createElement('template');
    template.innerHTML = html.trim();
    var block = template.content.firstElementChild;
    // As the theme does for ajax-loaded content: no scroll-in animation, which would otherwise
    // leave the block invisible.
    block.removeAttribute('data-cc-animate');
    block.querySelectorAll('[data-cc-animate]').forEach(function (el) {
      el.removeAttribute('data-cc-animate');
    });
    block.setAttribute(ADDED_ATTR, '');
    return document.importNode(block, true);
  }

  // Page numbers to show: first, last, and two either side of the current page, with longer
  // gaps as an ellipsis (as the app's bar: "1 2 3 ... 16"); a gap of one page shows its number.
  function barParts(page) {
    var shown = [];
    for (var k = 1; k <= pages; k += 1) {
      if (k === 1 || k === pages || Math.abs(k - page) <= 2) shown.push(k);
    }
    var parts = [];
    shown.forEach(function (k, i) {
      var prev = shown[i - 1];
      if (prev && k - prev === 2) parts.push(prev + 1);
      else if (prev && k - prev > 2) parts.push(null);
      parts.push(k);
    });
    return parts;
  }

  // The app's bar, renumbered for pages of 24 - same markup as snippets/pagination-control.
  function renderBar(bar, page) {
    var prev = bar.querySelector('.pagination__prev');
    var next = bar.querySelector('.pagination__next');
    if (prev) labels.prev = prev.textContent.trim();
    if (next) labels.next = next.textContent.trim();
    var linkBase = bar.querySelector('a[href]');
    var base = new URL(linkBase ? linkBase.href : window.location.href);
    base.hash = '';

    function link(k, text, extraClass) {
      var a = document.createElement('a');
      a.className = (extraClass ? extraClass + ' ' : '') + 'pagination__link text-current underline underline--on-hover';
      base.searchParams.set('page', String(appPageFor(k)));
      a.href = base.pathname + base.search;
      a.textContent = text;
      return a;
    }
    function sep() {
      var s = document.createElement('span');
      s.className = 'pagination__sep';
      s.textContent = '·';
      return s;
    }

    var items = [];
    if (page > 1) items.push(link(page - 1, labels.prev, 'pagination__prev'), sep());
    barParts(page).forEach(function (k) {
      var span = document.createElement('span');
      span.className = 'pagination__number';
      if (k === null) {
        span.textContent = '…';
      } else if (k === page) {
        span.className += ' underline underline--not-link';
        span.textContent = String(k);
      } else {
        span.appendChild(link(k, String(k)));
      }
      items.push(span);
    });
    if (page < pages) items.push(sep(), link(page + 1, labels.next, 'pagination__next'));

    bar.textContent = '';
    items.forEach(function (item, i) {
      if (i) bar.appendChild(document.createTextNode(' '));
      bar.appendChild(item);
    });
  }

  function apply(section, grid) {
    generation += 1;
    var run = generation;
    grid.setAttribute(DONE_ATTR, '');

    var bar = section.querySelector(BAR_SELECTOR);
    var appPage = appPageShown(bar);
    var own = Array.prototype.filter.call(grid.children, isProductBlock);
    var ownStart = (appPage - 1) * appPageSize;
    var ownEnd = ownStart + own.length;
    if (!bar || appPage < 1 || own.length !== Math.min(appPageSize, total - ownStart)) return; // not the page we expect

    var page = pageForAppPage(appPage);
    var from = (page - 1) * PER_PAGE;
    var to = Math.min(page * PER_PAGE, total);
    var before = Math.max(0, ownStart - from); // from the previous app page (only for old links)
    var after = Math.max(0, to - ownEnd); // from the next app page

    var savedBar = bar.innerHTML;
    own.forEach(function (el, i) {
      var at = ownStart + i;
      if (at < from || at >= to) el.classList.add(HIDDEN_CLASS);
    });
    if (pages > 1) renderBar(bar, page);
    else bar.classList.add(HIDDEN_CLASS);

    function restore() {
      grid.querySelectorAll('[' + ADDED_ATTR + ']').forEach(function (el) { el.remove(); });
      own.forEach(function (el) { el.classList.remove(HIDDEN_CLASS); });
      bar.classList.remove(HIDDEN_CLASS);
      bar.innerHTML = savedBar;
    }

    if (!before && !after) return;
    Promise.all([
      before ? appPageBlocks(appPage - 1) : Promise.resolve([]),
      after ? appPageBlocks(appPage + 1) : Promise.resolve([])
    ]).then(function (lists) {
      if (run !== generation || !grid.isConnected) return;
      var head = lists[0].slice(lists[0].length - before);
      var tail = lists[1].slice(0, after);
      if (head.length !== before || tail.length !== after) throw new Error('page ' + page + ' incomplete');
      var shown = {};
      own.forEach(function (el) { shown[el.getAttribute('data-product-id')] = true; });
      var headBlocks = head.map(toBlock);
      var tailBlocks = tail.map(toBlock);
      headBlocks.concat(tailBlocks).forEach(function (block) {
        if (shown[block.getAttribute('data-product-id')]) throw new Error('list changed');
      });
      var first = own[0];
      headBlocks.forEach(function (block) { grid.insertBefore(block, first); });
      tailBlocks.forEach(function (block) { grid.appendChild(block); });
    }).catch(function (err) {
      if (run !== generation || !grid.isConnected) return;
      restore(); // show the app's own page rather than an incomplete one
      if (window.console) console.warn('[cloud-search-full-rows]', err);
    });
  }

  // After swapping in another page the theme smooth-scrolls up to the grid. Going straight to
  // the theme's scroll target before the next paint keeps the height change (e.g. onto a short
  // last page) out of sight.
  function jumpToGrid(section) {
    var anchor = section.querySelector('[data-ajax-scroll-to]');
    var header = document.querySelector('.section-header');
    if (!anchor) return;
    var target = anchor.getBoundingClientRect().top + window.scrollY - (header ? header.clientHeight : 0);
    if (window.scrollY <= target + 1) return;
    try {
      window.scrollTo({ top: target, behavior: 'instant' });
    } catch (e) {
      window.scrollTo(0, target);
    }
    jumpedAt = Date.now();
  }

  function init() {
    var data = readAppData();
    var section = document.querySelector(SECTION_SELECTOR);
    if (!data || !section) return;
    appPageSize = data.pageSize;
    total = data.total;
    pages = Math.ceil(total / PER_PAGE);
    if (total <= appPageSize) return; // one app page: nothing to re-page

    var style = document.createElement('style');
    style.textContent = '.' + HIDDEN_CLASS + '{display:none!important}';
    document.head.appendChild(style);

    var firstGrid = true;
    function check() {
      var grid = section.querySelector(GRID_SELECTOR);
      if (!grid || grid.hasAttribute(DONE_ATTR)) return;
      apply(section, grid);
      if (!firstGrid) jumpToGrid(section);
      firstGrid = false;
    }

    check();

    // The theme swaps in a new grid (and the app's bar) on page links and Back.
    new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i += 1) {
        if (mutations[i].addedNodes.length) {
          check();
          return;
        }
      }
    }).observe(section, { childList: true, subtree: true });

    // After the jump a product card sits where "Next" was: swallow the second click of a quick
    // double-click so it doesn't open a product.
    section.addEventListener('click', function (evt) {
      if (Date.now() - jumpedAt < CLICK_GUARD_MS) {
        evt.preventDefault();
        evt.stopPropagation();
      }
    }, true);
  }

  window.CloudSearchFullRows = {
    state: function () {
      var section = document.querySelector(SECTION_SELECTOR);
      var grid = section && section.querySelector(GRID_SELECTOR);
      var current = section && section.querySelector(BAR_SELECTOR + ' .pagination__number.underline--not-link');
      return {
        page: current ? parseInt(current.textContent, 10) : null,
        pages: pages, perPage: PER_PAGE, appPageSize: appPageSize, total: total,
        shown: grid ? Array.prototype.filter.call(grid.children, function (el) {
          return el.tagName === 'PRODUCT-BLOCK' && !el.classList.contains(HIDDEN_CLASS);
        }).length : 0,
        added: grid ? grid.querySelectorAll('[' + ADDED_ATTR + ']').length : 0
      };
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
}());
